import Vue from 'vue';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { format } from 'date-fns';

import TableReservationApi from '@/extensions/tableReservation/store/api/tableReservation';
import {
    buildReservationInput,
    normalizeApiDate,
    normalizeReservation,
    pickAutoAssignment
} from '@/extensions/tableReservation/store/utils';
import { useSetupStore } from '@/stores/setup';
import { useTableReservationFloorPlanStore } from '@/stores/tableReservationFloorPlan';
import { useTableReservationSettingsStore } from '@/stores/tableReservationSettings';

export const RESERVATION_STATUS = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    SEATED: 'seated',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    NO_SHOW: 'no_show'
};

export const statusLabels = {
    pending: 'Ausstehend',
    confirmed: 'Bestätigt',
    seated: 'Platziert',
    completed: 'Abgeschlossen',
    cancelled: 'Storniert',
    no_show: 'No-Show'
};

export const statusColors = {
    pending: 'warning',
    confirmed: 'success',
    seated: '',
    completed: 'info',
    cancelled: 'danger',
    no_show: 'danger'
};

const defaultForm = {
    id: undefined,
    guestName: '',
    guestPhone: '',
    guestEmail: '',
    partySize: 2,
    date: '',
    time: '',
    duration: 90,
    tableIds: [],
    status: 'pending',
    notes: ''
};

const rules = {
    guestName: 'required',
    partySize: 'required',
    date: 'required',
    time: 'required'
};

function getCmsId() {
    const cmsId = _.get(useSetupStore(), 'activeCms.id');
    if (!cmsId) {
        throw new Error('No active CMS selected');
    }

    return parseInt(cmsId, 10);
}

function buildStats(reservations, date) {
    const filteredReservations = reservations.filter((reservation) => {
        return reservation.date === date;
    });

    return {
        total: filteredReservations.length,
        pending: filteredReservations.filter((reservation) => { return reservation.status === 'pending'; }).length,
        confirmed: filteredReservations.filter((reservation) => { return reservation.status === 'confirmed'; }).length,
        seated: filteredReservations.filter((reservation) => { return reservation.status === 'seated'; }).length,
        completed: filteredReservations.filter((reservation) => { return reservation.status === 'completed'; }).length,
        cancelled: filteredReservations.filter((reservation) => { return reservation.status === 'cancelled'; }).length,
        noShow: filteredReservations.filter((reservation) => { return reservation.status === 'no_show'; }).length,
        totalGuests: filteredReservations
            .filter((reservation) => { return reservation.status !== 'cancelled' && reservation.status !== 'no_show'; })
            .reduce((sum, reservation) => { return sum + reservation.partySize; }, 0)
    };
}

export const useTableReservationStore = defineStore('tableReservation', {
    state: function () {
        return {
            loading: false,
            reservations: [],
            form: _.cloneDeep(defaultForm),
            rules: _.cloneDeep(rules),
            filters: {
                date: format(new Date(), 'yyyy-MM-dd'),
                status: ''
            },
            loadedDate: null,
            loadedCmsId: null
        };
    },

    getters: {
        filteredReservations: function (state) {
            let filtered = state.reservations;

            if (state.filters.date) {
                filtered = filtered.filter((reservation) => {
                    return reservation.date === state.filters.date;
                });
            }

            if (state.filters.status) {
                filtered = filtered.filter((reservation) => {
                    return reservation.status === state.filters.status;
                });
            }

            return _.orderBy(filtered, ['time', 'guestName'], ['asc', 'asc']);
        },

        reservationsByDate: function (state) {
            return function (dateStr) {
                return _.orderBy(
                    state.reservations.filter((reservation) => {
                        return reservation.date === dateStr;
                    }),
                    ['time', 'guestName'],
                    ['asc', 'asc']
                );
            };
        },

        reservationsByTableAndDate: function (state) {
            return function (tableId, dateStr) {
                return state.reservations.filter((reservation) => {
                    return (reservation.tableIds || []).includes(tableId)
                        && reservation.date === dateStr
                        && reservation.status !== 'cancelled';
                });
            };
        },

        currentStats: function (state) {
            return buildStats(state.reservations, state.filters.date);
        },

        todayStats: function (state) {
            return buildStats(state.reservations, state.filters.date);
        }
    },

    actions: {
        hydrateBoard: function (board) {
            const floorPlanStore = useTableReservationFloorPlanStore();
            const settingsStore = useTableReservationSettingsStore();

            this.reservations = _.orderBy(
                (_.get(board, 'reservations', []) || []).map(normalizeReservation),
                ['date', 'time', 'guestName'],
                ['asc', 'asc', 'asc']
            );

            floorPlanStore.hydrateBoard({
                rooms: _.get(board, 'rooms', []),
                tables: _.get(board, 'tables', []),
                fixtures: _.get(board, 'fixtures', []),
                combinations: _.get(board, 'combinations', [])
            });
            settingsStore.hydrateSettings(_.get(board, 'settings', {}));
        },

        syncReservationToCurrentBoard: function (reservation) {
            const normalizedReservation = normalizeReservation(reservation);
            const index = _.findIndex(this.reservations, (candidate) => {
                return candidate.id === normalizedReservation.id;
            });

            if (normalizedReservation.date !== this.filters.date) {
                if (index >= 0) {
                    this.reservations.splice(index, 1);
                }
                return null;
            }

            if (index >= 0) {
                Vue.set(this.reservations, index, normalizedReservation);
            } else {
                this.reservations.push(normalizedReservation);
            }

            this.reservations = _.orderBy(this.reservations, ['date', 'time', 'guestName'], ['asc', 'asc', 'asc']);
            return normalizedReservation;
        },

        removeReservationFromBoard: function (id) {
            this.reservations = this.reservations.filter((reservation) => {
                return reservation.id !== id;
            });
        },

        setFilterDate: function (date) {
            const requestedDate = normalizeApiDate(date);
            if (!requestedDate) {
                return Promise.reject(new Error('Invalid date'));
            }

            this.filters.date = requestedDate;
            return this.fetchBoard(requestedDate);
        },

        setFilterStatus: function (status) {
            this.filters.status = status;
        },

        ensureBoard: async function (date) {
            const requestedDate = date || this.filters.date;
            const cmsId = getCmsId();

            if (this.loadedDate === requestedDate && this.loadedCmsId === cmsId) {
                return;
            }

            return this.fetchBoard(requestedDate);
        },

        fetchBoard: async function (date) {
            const requestedDate = normalizeApiDate(date || this.filters.date);
            const cmsId = getCmsId();

            if (!requestedDate) {
                throw new Error('Invalid date');
            }

            this.loading = true;

            try {
                const board = await TableReservationApi.fetchBoard(cmsId, requestedDate);
                this.filters.date = requestedDate;
                this.loadedDate = requestedDate;
                this.loadedCmsId = cmsId;
                this.hydrateBoard(board);
                return board;
            } finally {
                this.loading = false;
            }
        },

        fetchReservation: async function (id) {
            const reservation = await TableReservationApi.fetchReservation(id);
            this.form = normalizeReservation(reservation);
            return this.form;
        },

        createReservation: async function (reservationData) {
            const cmsId = getCmsId();
            const reservation = normalizeReservation(_.assign({}, _.cloneDeep(defaultForm), reservationData || {}));
            const savedReservation = await TableReservationApi.createReservation(
                buildReservationInput(reservation, cmsId)
            );

            return this.syncReservationToCurrentBoard(savedReservation);
        },

        saveReservation: async function () {
            const settingsStore = useTableReservationSettingsStore();
            const floorPlanStore = useTableReservationFloorPlanStore();

            const autoAssignment = { attempted: false, success: false };
            const hasManualSelection = (this.form.tableIds || []).length > 0;
            const isAutomaticMode = _.get(settingsStore, 'settings.tablePlacementMode') === 'automatic';

            if (isAutomaticMode && !hasManualSelection) {
                autoAssignment.attempted = true;
                const assignment = pickAutoAssignment(
                    floorPlanStore.tables,
                    floorPlanStore.combinations,
                    this.reservations,
                    this.form
                );

                if (assignment && (assignment.tableIds || []).length > 0) {
                    this.form.tableIds = assignment.tableIds.slice();
                    autoAssignment.success = true;
                }
            }

            let savedReservation;

            if (this.form.id) {
                savedReservation = await TableReservationApi.updateReservation(
                    buildReservationInput(this.form)
                );
            } else {
                savedReservation = await TableReservationApi.createReservation(
                    buildReservationInput(this.form, getCmsId())
                );
            }

            const normalizedReservation = normalizeReservation(savedReservation);
            this.form = _.cloneDeep(normalizedReservation);
            this.syncReservationToCurrentBoard(savedReservation);
            return { reservation: normalizedReservation, autoAssignment: autoAssignment };
        },

        deleteReservation: async function (id) {
            await TableReservationApi.deleteReservation(id);
            this.removeReservationFromBoard(String(id));
        },

        applyReservationPatch: async function (id, patch) {
            const index = _.findIndex(this.reservations, (reservation) => {
                return reservation.id === String(id);
            });

            if (index < 0) {
                throw new Error('Reservation not found');
            }

            const previousReservation = _.cloneDeep(this.reservations[index]);
            const nextReservation = normalizeReservation(_.assign({}, previousReservation, patch));
            Vue.set(this.reservations, index, nextReservation);

            try {
                const savedReservation = await TableReservationApi.updateReservation(
                    buildReservationInput(nextReservation)
                );

                return this.syncReservationToCurrentBoard(savedReservation);
            } catch (error) {
                Vue.set(this.reservations, index, previousReservation);
                throw error;
            }
        },

        updateStatus: function (id, status) {
            return this.applyReservationPatch(id, { status: status });
        },

        updateReservationTime: function (id, newTime) {
            return this.applyReservationPatch(id, { time: newTime });
        },

        moveReservation: function (id, patch) {
            return this.applyReservationPatch(id, patch);
        },

        assignTable: function (reservationId, tableId) {
            const reservation = this.reservations.find((candidate) => {
                return candidate.id === String(reservationId);
            });

            if (!reservation) {
                return Promise.reject(new Error('Reservation not found'));
            }

            return this.applyReservationPatch(reservationId, {
                tableIds: _.uniq([].concat(reservation.tableIds || [], [String(tableId)]))
            });
        },

        unassignTable: function (reservationId, tableId) {
            const reservation = this.reservations.find((candidate) => {
                return candidate.id === String(reservationId);
            });

            if (!reservation) {
                return Promise.reject(new Error('Reservation not found'));
            }

            return this.applyReservationPatch(reservationId, {
                tableIds: tableId
                    ? (reservation.tableIds || []).filter((currentTableId) => {
                        return currentTableId !== String(tableId);
                    })
                    : []
            });
        },

        setFormField: function (field, value) {
            Vue.set(this.form, field, value);
        },

        resetForm: function (overrides) {
            const settingsStore = useTableReservationSettingsStore();

            this.form = _.assign({}, _.cloneDeep(defaultForm), {
                date: this.filters.date || format(new Date(), 'yyyy-MM-dd'),
                duration: _.get(settingsStore, 'settings.defaultDuration', 90)
            }, overrides || {});
        },

        reset: function () {
            this.form = _.cloneDeep(defaultForm);
            this.reservations = [];
            this.filters = {
                date: format(new Date(), 'yyyy-MM-dd'),
                status: ''
            };
            this.loadedDate = null;
            this.loadedCmsId = null;
        }
    }
});
